import { Injectable, inject } from '@angular/core';
import { EnrollhereApiService } from '../../common/enrollhere-api/enrollhere-api';
import {
  Payment,
  PayoutType,
  UserIdContainer,
  typeFromJSON,
} from '@enrollhere/classes';

/** Convert payoutType string to PayoutType enum value */
function payoutTypeFromJSON(value: unknown): PayoutType | undefined {
  if (typeof value === 'number') return value as PayoutType;
  if (typeof value !== 'string') return undefined;

  const mapping: Record<string, PayoutType> = {
    PT_AUTO_CALCULATED: PayoutType.PT_AUTO_CALCULATED,
    PT_SEND_PAYOUT: PayoutType.PT_SEND_PAYOUT,
    PT_SEND_LOAN_ADVANCE: PayoutType.PT_SEND_LOAN_ADVANCE,
    PT_RECEIVE_LOAN_REPAYMENT: PayoutType.PT_RECEIVE_LOAN_REPAYMENT,
    PT_RECEIVE_GENERAL_PAYMENT: PayoutType.PT_RECEIVE_GENERAL_PAYMENT,
    PT_USAGE: PayoutType.PT_USAGE,
    PT_TOP_UP: PayoutType.PT_TOP_UP,
    PT_AUTO_TOP_UP: PayoutType.PT_AUTO_TOP_UP,
    PT_TRANSFER: PayoutType.PT_TRANSFER,
  };
  return mapping[value];
}

/** Extended agent type with enriched data from API */
export interface EnrichedAgent extends UserIdContainer {
  firstName?: string;
  lastName?: string;
  npn?: string;
}

/** Payout record from the API with enriched agent data */
export interface EnrichedPayout extends Omit<Payment, 'agent'> {
  agent?: EnrichedAgent;
}

export interface PayoutSearchParams {
  userIds?: string[];
  accountIds?: string[]; // filter by transaction.account.id
  payoutTypes?: string[];
  paymentDateStart?: number;
  paymentDateEnd?: number;
  limit?: number;
  offset?: number;
}

export interface PayoutSearchResponse {
  payouts: EnrichedPayout[];
  total: number;
  limit: number;
  offset: number;
}

export interface PayoutAggregationsParams {
  filter?: {
    userIds?: string[];
    accountIds?: string[]; // filter by transaction.account.id
    payoutTypes?: string[];
    paymentDateStart?: number;
    paymentDateEnd?: number;
  };
  requested?: string[];
  options?: Record<string, { size?: number }>;
}

export interface PayoutFacetBucket {
  id: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  count: number;
}

export interface PayoutAggregationsResponse {
  userIds?: PayoutFacetBucket[];
  payoutTypes?: PayoutFacetBucket[];
  paymentDateBounds?: {
    min: number | null;
    max: number | null;
  };
}

interface RawPayoutSearchResponse {
  payouts: unknown[];
  total: number;
  limit: number;
  offset: number;
}

@Injectable({ providedIn: 'root' })
export class PayoutApiService {
  private apiService = inject(EnrollhereApiService);

  async searchPayouts(
    params: PayoutSearchParams
  ): Promise<PayoutSearchResponse> {
    const body = {
      ...params,
      limit: params.limit ?? 20,
      offset: params.offset ?? 0,
    };

    const result = await this.apiService.post<RawPayoutSearchResponse>(
      'payments/payout/search',
      body
    );

    return {
      payouts: result.payouts.map((p: unknown) => {
        const raw = p as Record<string, unknown>;
        const payment = Payment.fromJSON(p);

        // Convert payoutType from string to enum if needed
        const payoutType = raw['payoutType']
          ? payoutTypeFromJSON(raw['payoutType'])
          : payment.payoutType;

        // Preserve enriched agent data and source with properly converted type enum
        const rawSource = raw['source'] as Record<string, unknown> | undefined;
        const source = rawSource
          ? {
              ...rawSource,
              type: rawSource['type']
                ? typeFromJSON(rawSource['type'])
                : undefined,
            }
          : payment.source;

        return {
          ...payment,
          payoutType,
          source,
        } as EnrichedPayout;
      }),
      total: result.total || 0,
      limit: result.limit || params.limit || 20,
      offset: result.offset || params.offset || 0,
    };
  }

  async getAggregations(
    payload: PayoutAggregationsParams
  ): Promise<PayoutAggregationsResponse> {
    return this.apiService.post<PayoutAggregationsResponse>(
      'payments/payout/aggregations',
      payload
    );
  }
}
